import type { DatabaseEngineId } from '@/core/engines';
import type { SqlSnippetTemplate } from './sqlSnippetTemplateTypes';
import { byEngine } from './sqlSnippetTemplateUtils';

export function getBuiltInSqlSnippetTemplatesUtility(engineId: DatabaseEngineId): readonly SqlSnippetTemplate[] {
  const templates: SqlSnippetTemplate[] = [
    {
      id: 'transaction',
      label: 'Transaction (BEGIN/COMMIT)',
      detail: 'Built-in snippet',
      documentation: 'Transaction template',
      filterText: 'begin transaction commit rollback',
      insertText: byEngine(engineId, {
        mssql:
          'BEGIN TRY\n  BEGIN TRAN;\n\n  ${1:-- statements}\n\n  COMMIT;\nEND TRY\nBEGIN CATCH\n  IF @@TRANCOUNT > 0 ROLLBACK;\n  THROW;\nEND CATCH;',
        pgsql: 'BEGIN;\n\n${1:-- statements}\n\nCOMMIT;\n-- ROLLBACK;',
        oracle: 'BEGIN\n  ${1:-- statements}\n  COMMIT;\nEXCEPTION\n  WHEN OTHERS THEN\n    ROLLBACK;\n    RAISE;\nEND;\n/',
      }),
    },
    {
      id: 'explain',
      label: byEngine(engineId, { mssql: 'Execution plan (STATISTICS XML)', pgsql: 'EXPLAIN (ANALYZE, BUFFERS)', sqlite: 'EXPLAIN QUERY PLAN', mysql: 'EXPLAIN FORMAT=JSON', oracle: 'EXPLAIN PLAN FOR …' }),
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, {
        mssql: 'Execution plan template (SQL Server)',
        pgsql: 'Explain plan template (PostgreSQL)',
        sqlite: 'Explain plan template (SQLite)',
        mysql: 'Explain plan template (MySQL)',
        oracle: 'Explain plan template (Oracle)',
      }),
      filterText: byEngine(engineId, { mssql: 'statistics xml showplan', pgsql: 'explain analyze buffers', sqlite: 'explain query plan', mysql: 'explain format json analyze', oracle: 'explain plan for dbms_xplan' }),
      insertText: byEngine(engineId, {
        mssql: 'SET STATISTICS XML ON;\n${1:-- query}\nSET STATISTICS XML OFF;',
        pgsql: 'EXPLAIN (ANALYZE, BUFFERS)\n${1:-- query};',
        sqlite: 'EXPLAIN QUERY PLAN\n${1:-- query};',
        mysql: 'EXPLAIN FORMAT=JSON\n${1:-- query};',
        oracle: 'EXPLAIN PLAN FOR\n${1:SELECT * FROM table};\n\nSELECT * FROM TABLE(DBMS_XPLAN.DISPLAY());',
      }),
    },
    {
      id: 'comment-header',
      label: 'Comment header',
      detail: 'Built-in snippet',
      documentation: 'Comment header template',
      filterText: 'comment header',
      insertText: '-- ${1:Title}\n-- ${2:Description}\n',
    },
  ];

  if (engineId === 'pgsql') {
    templates.push(
      {
        id: 'pg19-repack',
        label: 'REPACK (CONCURRENTLY) … (PG19)',
        detail: 'Built-in snippet',
        documentation: 'Rebuild a table to reclaim space and defragment, without VACUUM FULL\'s exclusive lock (PostgreSQL 19+). Drop (CONCURRENTLY) for the plain form, which takes a stronger lock.',
        filterText: 'repack concurrently vacuum rebuild defragment pg19',
        insertText: 'REPACK (CONCURRENTLY) ${1:table};',
      },
      {
        id: 'pg19-wait-for',
        label: 'WAIT FOR LSN … (PG19)',
        detail: 'Built-in snippet',
        documentation: 'Block until WAL replay/flush reaches a target LSN — useful for read-after-write consistency against a replica (PostgreSQL 19+).',
        filterText: 'wait for lsn replica flush primary standby pg19',
        insertText: 'WAIT FOR LSN ${1:\'lsn\'} WITH (MODE ${2:\'primary_flush\'}, TIMEOUT ${3:\'1000\'});',
      },
    );
  }

  if (engineId === 'oracle') {
    templates.push(
      {
        id: 'v-session',
        label: 'SELECT * FROM V$SESSION',
        detail: 'Built-in snippet',
        documentation: 'Query active sessions (Oracle)',
        filterText: 'v$session active sessions',
        insertText:
          'SELECT SID, SERIAL#, USERNAME, STATUS, OSUSER, MACHINE, PROGRAM, SQL_ID,\n  LAST_CALL_ET, LOGON_TIME\nFROM V$SESSION\nWHERE TYPE = \'USER\'\n  AND STATUS = \'${1:ACTIVE}\'\nORDER BY LAST_CALL_ET DESC;',
      },
      {
        id: 'user-objects',
        label: 'SELECT * FROM USER_OBJECTS',
        detail: 'Built-in snippet',
        documentation: 'List user objects (Oracle)',
        filterText: 'user objects status invalid',
        insertText:
          'SELECT OBJECT_NAME, OBJECT_TYPE, STATUS, CREATED, LAST_DDL_TIME\nFROM USER_OBJECTS\nWHERE OBJECT_TYPE = \'${1:TABLE}\'\nORDER BY OBJECT_NAME;',
      },
      {
        id: 'alter-session',
        label: 'ALTER SESSION SET …',
        detail: 'Built-in snippet',
        documentation: 'Alter session parameters (Oracle)',
        filterText: 'alter session set current_schema nls',
        insertText: 'ALTER SESSION SET ${1:CURRENT_SCHEMA} = ${2:schema_name};',
      },
      {
        id: 'dbms-output',
        label: 'DBMS_OUTPUT.PUT_LINE …',
        detail: 'Built-in snippet',
        documentation: 'Print output using DBMS_OUTPUT (Oracle)',
        filterText: 'dbms_output put_line print',
        insertText: 'BEGIN\n  DBMS_OUTPUT.PUT_LINE(\'${1:Hello World}\');\nEND;\n/',
      },
      {
        id: 'dbms-metadata',
        label: 'DBMS_METADATA.GET_DDL …',
        detail: 'Built-in snippet',
        documentation: 'Get DDL for an object (Oracle)',
        filterText: 'dbms_metadata get_ddl',
        insertText: 'SELECT DBMS_METADATA.GET_DDL(\'${1:TABLE}\', \'${2:OBJECT_NAME}\', \'${3:SCHEMA}\')\nFROM DUAL;',
      },
    );
  }

  return templates;
}
